import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';

interface User {
  email: string;
  name: string;
  role: string;
  lastLogin: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  error: string | null;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
  login: async () => false,
  logout: () => {},
  error: null,
});

export const useAuth = () => useContext(AuthContext);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Check for token in localStorage on initial load
    const storedToken = localStorage.getItem('abhi_security_token');
    if (storedToken) {
      setToken(storedToken);
      fetchUserProfile(storedToken);
    } else {
      setIsLoading(false);
    }
  }, []);

  const fetchUserProfile = async (authToken: string) => {
    try {
      // For demo purposes, we'll just simulate a successful authentication
      // In a real app, you would validate the token with your backend
      const userData = {
        email: import.meta.env.VITE_ADMIN_EMAIL || "security@abhipvtltd.com",
        name: "Security Operations Center",
        role: "Admin",
        lastLogin: new Date().toISOString()
      };
      
      setUser(userData);
      setIsLoading(false);
    } catch (err) {
      console.error('Failed to fetch user profile:', err);
      localStorage.removeItem('abhi_security_token');
      setToken(null);
      setUser(null);
      setIsLoading(false);
    }
  };

  const login = async (email: string, password: string): Promise<boolean> => {
    setError(null);
    setIsLoading(true);
    
    try {
      // Get credentials from environment variables, with fallbacks
      const correctEmail = import.meta.env.VITE_ADMIN_EMAIL || "areyousure@dum.com";
      const correctPassword = import.meta.env.VITE_ADMIN_PASSWORD || "ook-who-trying-to-hack-lol";
      
      if (email === correctEmail && password === correctPassword) {
        // Create a fake token
        const fakeToken = "jwt-" + Math.random().toString(36).substring(2);
        
        const userData = {
          email: correctEmail,
          name: "Security Operations Center",
          role: "Admin",
          lastLogin: new Date().toISOString()
        };
        
        setUser(userData);
        setToken(fakeToken);
        localStorage.setItem('abhi_security_token', fakeToken);
        setIsLoading(false);
        return true;
      } else {
        setError('Invalid email or password');
        setIsLoading(false);
        return false;
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('An error occurred during login. Please try again.');
      setIsLoading(false);
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem('abhi_security_token');
    setUser(null);
    setToken(null);
  };

  const value = {
    user,
    token,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    error,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
